import { createContext, useEffect, useState, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { fetchCurrentUser, type AdminUser } from '@/lib/auth'

interface AuthState {
  user: AdminUser | null
  loading: boolean
  isAuthenticated: boolean
}

export const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    isAuthenticated: false,
  })

  useEffect(() => {
    let cancelled = false

    fetchCurrentUser()
      .then((user) => {
        if (cancelled) return
        setState({ user, loading: false, isAuthenticated: true })
      })
      .catch(() => {
        if (cancelled) return
        // Cloudflare Access is the security gate — if /me fails,
        // still render the app (just without user info)
        setState({ user: null, loading: false, isAuthenticated: false })
      })

    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (state.user) {
      Sentry.setUser({ id: state.user.id, email: state.user.email })
    } else {
      Sentry.setUser(null)
    }
  }, [state.user])

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}
